import { useCallback, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { Values } from '@lib/interfaces/form';

interface UseFormPersistValuesProps {
  formId: string;
  persistForm: boolean;
  persistFormSeconds?: number;
}

const useFormPersistValues = ({
  formId,
  persistForm,
  persistFormSeconds = 0,
}: UseFormPersistValuesProps) => {
  const [form, setForm] = useLocalStorage<
    | {
        timestamp: number;
        values: Values;
      }
    | undefined
  >(formId, undefined);

  const setPersistedValues = useCallback(
    (values: Values) => {
      if (!persistForm) {
        return;
      }
      const dataToPersist = {
        values,
        timestamp: new Date().getTime(),
      };
      setForm(dataToPersist);
    },
    [persistForm, setForm],
  );

  const getPersistedValues = (): Values => {
    if (form?.timestamp && form?.values) {
      const currentTime = new Date().getTime();
      const timeElapsed = (currentTime - form.timestamp) / 1000;

      if (!persistFormSeconds || timeElapsed <= persistFormSeconds) {
        return form.values;
      }
      // Time expired, remove from storage
      setForm(undefined);
    }
    return {};
  };

  useEffect(() => {
    if (!persistForm && form) {
      setForm(undefined);
    }
  }, [form, persistForm, setForm]);

  return { setPersistedValues, getPersistedValues };
};

export default useFormPersistValues;
